/**
 * Zod schemas for the `notifications` collection.
 *
 * Push + email delivery records, fanned out to recipients via the
 * `sendNotification` Cloud Function. The CF writes the delivery counters
 * (recipientCount/pushTokenCount/etc.) at fan-out time — the
 * `affectedKeys` guard in firestore.rules blocks client writes to those.
 *
 * Audience targeting (ADR-120): notifications reference one Audience via
 * `audienceId`, the same authoring shape sessions and travel items use.
 * Unlike sessions/travel, notification targeting resolves once at send
 * time and freezes into `targetedPersonIds[]`, because notification
 * delivery is a historical event. Cascading triggers are gated on
 * `status` being `draft` or `scheduled` and skip sent notifications.
 * Security rules check `request.auth.token.personId in targetedPersonIds`
 * (ADR-137).
 */

import { z } from 'zod';
import type { TimestampLike } from '../types/common';

export const notificationStatusSchema = z.enum([
  'draft',
  'scheduled',
  'sent',
  'cancelled',
]);
export const notificationChannelSchema = z.enum(['push', 'email']);
export const notificationTriggerTypeSchema = z.enum([
  'manual',
  'automatic',
  /** Created by the processScheduledTravelReminders cron (ADR-094 PR 3). */
  'travel_reminder',
]);

export const notificationDocumentSchema = z.object({
  eventId: z.string(),
  /** Auth uid of the organizer or staff member who created this notification. */
  createdByUserId: z.string(),
  /**
   * personId of the creator, stamped by the staff-area callables
   * (ADR-196) so the app's sent history can show a name. Absent on
   * notifications written by the portal, crons and bulk import.
   */
  createdByPersonId: z.string().nullable().optional(),

  /** Delivery channels for this notification. */
  channels: z.array(notificationChannelSchema),

  /** Push notification content. */
  title: z.string(),
  body: z.string(),
  /** Deep link path, e.g. "events/abc123/sessions/xyz789". */
  deepLink: z.string().nullable(),

  /** Email-specific content. */
  emailSubject: z.string().nullable(),
  emailBodyHtml: z.string().nullable(),

  /**
   * Audience targeting — single Audience reference. Resolved to a flat
   * personId set at send time by reading the linked audience's
   * `resolvedPersonIds[]` (ADR-137). The audience can be a custom group,
   * a role audience, a personal audience, or an anonymous composition.
   */
  audienceId: z.string(),

  /**
   * CF-managed frozen recipient personId cache written by sendNotification
   * before receipts are created (ADR-137). Security rules gate attendee
   * reads on `request.auth.token.personId in targetedPersonIds`. Cascade
   * triggers skip notifications where status is `sent` or `cancelled` so
   * the historical recipient set remains frozen even if the linked
   * audience is later mutated.
   */
  targetedPersonIds: z.array(z.string()).default([]),

  status: notificationStatusSchema,
  scheduledAt: z.custom<TimestampLike>().nullable(),
  sentAt: z.custom<TimestampLike>().nullable(),

  /** Delivery counters written by the sendNotification CF at fan-out time. */
  recipientCount: z.number().nullable(),
  pushTokenCount: z.number().nullable(),
  pushSuccessCount: z.number().nullable(),
  pushFailureCount: z.number().nullable(),

  triggerType: notificationTriggerTypeSchema,
});
